import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Card from '../../components/Card.jsx';
import { authContext } from '../../context/index.js';
import { Loader2 } from 'lucide-react';

const TeacherStudents = () => {
  const { backendUrl } = useContext(authContext);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatEnrollmentDate = (enrolledAt) => {
    if (!enrolledAt) return 'N/A';
    const date = new Date(enrolledAt);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    let isMounted = true;
    const fetchStudents = async () => {
      setLoading(true);
      try {
        // Step 1: Get all courses created by this teacher
        const coursesRes = await axios.get(backendUrl + '/api/course/my-courses', { withCredentials: true });
        if (!coursesRes.data.success) throw new Error('Failed to fetch courses');
        const courses = coursesRes.data.courses || [];
        
        // Step 2: For each course, get enrollments (not just students)
        let enrollmentsList = [];
        for (const course of courses) {
          try {
            // This endpoint should return enrollment data with student info
            const enrollRes = await axios.get(backendUrl + `/api/enroll/${course._id}/students`, { withCredentials: true });
            if (!enrollRes.data.success) continue;
            
            // The response contains students array, but we need enrollment dates
            // Let's try to get enrollment data which includes dates
            const students = enrollRes.data.students || [];
            
            for (const student of students) {
              enrollmentsList.push({
                _id: student._id,
                name: student.name || 'N/A',
                email: student.email || 'N/A',
                courseTitle: course.title,
                courseId: course._id,
                enrolledAt: student.enrolledAt, // Now properly returned from backend
                progress: student.progress || 0,
                completed: student.completed || false
              });
            }
          } catch (error) {
            console.error(`Error fetching students for course ${course._id}:`, error);
          }
        }
        
        if (isMounted) setStudents(enrollmentsList);
      } catch (err) {
        console.error('Error fetching students:', err);
        if (isMounted) setStudents([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStudents();
    return () => { isMounted = false; };
  }, [backendUrl]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold text-secondary-900">Students Enrolled</h1>
        <div className="text-secondary-600">All students that enrolled in your courses</div>
      </div>
      <Card>
        {loading ? (
          <div className="min-h-[250px] flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <span>Loading students...</span>
          </div>
        ) : students.length === 0 ? (
          <div className="py-10 text-center text-secondary-600">No students are enrolled in your courses yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full mt-2 border">
              <thead>
                <tr className="bg-secondary-100">
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Course</th>
                  <th className="p-2 text-left">Progress</th>
                  <th className="p-2 text-left">Enrollment Date</th>
                </tr>
              </thead>
              <tbody>
                {students.map((stu, idx) => (
                  <tr key={stu._id + '-' + stu.courseId} className="border-t hover:bg-primary-50">
                    <td className="p-2">{stu.name || 'N/A'}</td>
                    <td className="p-2">{stu.email || 'N/A'}</td>
                    <td className="p-2">{stu.courseTitle || 'N/A'}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full" 
                            style={{ width: `${stu.progress || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-secondary-600">{stu.progress || 0}%</span>
                      </div>
                    </td>
                    <td className="p-2">{formatEnrollmentDate(stu.enrolledAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TeacherStudents;
